/**
 * Ultimadesk : animations de navigation, sur toutes les pages (styles : custom.css, section 17).
 *
 * 1. Apparition au défilement : les blocs des sections apparaissent en montant légèrement quand
 *    ils arrivent à l'écran, une seule fois. Les éléments côte à côte (cartes, étapes, colonnes)
 *    apparaissent l'un après l'autre. Rien n'est masqué avant que ce script tourne : sans lui,
 *    la page s'affiche normalement.
 * 2. Image qui glisse de la carte produit vers la fiche (View Transitions entre deux pages,
 *    navigateurs compatibles) : la photo de la carte cliquée prend la place de la première image
 *    de la galerie, et y retourne au retour arrière. Ailleurs, le fondu de 180 ms reste seul.
 * 3. Fond sombre partagé : à l'approche d'une section d'avis sur fond sombre (ID d'ancre « avis »)
 *    ou d'une section marquée « ud-immersive », la section qui la précède et celle qui la suit
 *    prennent son nuancier ; vers la fin de la section, la suivante retrouve le sien.
 *
 * Rien ne bouge si le visiteur a demandé moins d'animations, ni dans l'éditeur de thème.
 */

const motionAllowed =
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.Shopify?.designMode;

/* 1. Apparition au défilement ------------------------------------------------------------ */

// Sections laissées telles quelles : zone produit (collante), menu d'ancres, panier, recherche, applications
const SKIP_SECTIONS = ':has(.product-section, .cart__inner, .main-search, ud-product-nav, .shopify-app-block)';
// Blocs qui apparaissent d'un seul tenant : textes, boutons, carrousels, défilants, tableaux, formulaires
const ATOMIC =
  '.text-block, .button-block, .slider-block, .splide, slider-component, .marquee, .comparison-table__table-wrapper, form, table';
const NOT_CONTENT = 'script, style, template, link, noscript';
// Un bloc apparaît quand son haut passe à 92 % de la hauteur de l'écran
const REVEAL_LINE = 0.92;

function visibleChildren(element) {
  return [...element.children].filter(
    (child) => !child.matches(NOT_CONTENT) && child.getClientRects().length > 0 && child.offsetHeight > 0
  );
}

// Deux premiers éléments sur la même ligne (des boîtes, pas du texte en ligne)
function sideBySide(elements) {
  if (elements.some((element) => getComputedStyle(element).display === 'inline')) return false;
  const a = elements[0].getBoundingClientRect();
  const b = elements[1].getBoundingClientRect();
  return Math.abs(a.top - b.top) < 4 && Math.abs(a.left - b.left) > 4;
}

// Rangée qui défile à l'horizontale (onglets, cartes sur mobile) : ses éléments hors de l'écran
// n'arriveraient jamais en vue verticalement, elle apparaît donc d'un seul tenant
function scrollsSideways(element, block) {
  for (let node = element; node; node = node.parentElement) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowX)) return true;
    if (node === block) break;
  }
  return false;
}

// Un bloc qui aligne plusieurs éléments côte à côte les fait apparaître un par un
// (on traverse au plus trois enveloppes à un seul enfant) ; une FAQ, question par question.
function expand(block) {
  if (block.matches(ATOMIC)) return [block];
  if (block.matches('.accordions')) return visibleChildren(block);

  let element = block;
  for (let depth = 0; depth < 3; depth += 1) {
    const children = visibleChildren(element);
    if (children.length === 1 && !children[0].matches(ATOMIC)) {
      element = children[0];
      continue;
    }
    if (children.length > 1 && sideBySide(children)) {
      return scrollsSideways(element, block) ? [block] : children;
    }
    break;
  }
  return [block];
}

function revealTargets() {
  const sections = document.querySelectorAll('.shopify-section[id^="shopify-section-template--"]');

  return [...sections]
    .filter((section) => !section.matches(SKIP_SECTIONS))
    .flatMap((section) => {
      const [root] = visibleChildren(section);
      if (!root) return [];
      const inner = root.matches('[class*="__inner"]') ? root : root.querySelector('[class*="__inner"]');
      // Section sans conteneur de blocs (bannière, carrousel) : elle apparaît d'un seul tenant
      if (!inner || inner.closest('.splide') || inner.matches('[class*="banner"]')) return [root];
      return visibleChildren(inner).flatMap(expand);
    })
    // Les éléments qui ont déjà leur propre animation (visuels des sections premium) gardent la leur
    .filter((element) => getComputedStyle(element).animationName === 'none');
}

function initReveal() {
  const line = window.innerHeight * REVEAL_LINE;
  // Ce qui est déjà à l'écran (ou au-dessus) au chargement n'est jamais masqué
  const hidden = revealTargets().filter((element) => element.getBoundingClientRect().top > line);
  if (hidden.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      const arriving = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top || a.boundingClientRect.left - b.boundingClientRect.left);

      let order = 0;
      for (const { target, boundingClientRect } of arriving) {
        observer.unobserve(target);

        // Bloc atteint par le haut (retour vers le haut après un saut d'ancre) : affiché sans animation
        if (boundingClientRect.top < 0) {
          target.classList.remove('ud-reveal');
          continue;
        }

        // Les blocs qui arrivent ensemble apparaissent l'un après l'autre (90 ms d'écart, 4 au plus)
        const delay = Math.min(order, 4) * 90;
        order += 1;
        target.style.setProperty('--ud-reveal-delay', `${delay}ms`);
        target.classList.add('ud-reveal--run');
        target.classList.remove('ud-reveal');
        setTimeout(() => {
          target.classList.remove('ud-reveal--run');
          target.style.removeProperty('--ud-reveal-delay');
        }, 1000 + delay);
      }
    },
    { rootMargin: `0px 0px -${Math.round((1 - REVEAL_LINE) * 100)}% 0px` }
  );

  for (const element of hidden) {
    element.classList.add('ud-reveal');
    observer.observe(element);
  }
}

/* 2. Image qui glisse de la carte produit vers la fiche ------------------------------------- */

const IMAGE_NAME = 'ud-product-image';
// Première image de la galerie de la fiche : nommée dans custom.css, c'est le point d'arrivée
const MAIN_MEDIA = "body[data-template='product'] [id$='__main'] [data-ref='main-slider'] .splide__slide:first-child";
const namedElements = new Set();
let lastCard = null;

// Chemin « /products/poignee » d'une URL de la boutique (sans collection, variante ni paramètres)
function productPath(href) {
  if (!href) return null;
  try {
    const url = new URL(href, window.location.href);
    const start = url.pathname.indexOf('/products/');
    if (url.origin !== window.location.origin || start === -1) return null;
    return url.pathname.slice(start).replace(/\/$/, '');
  } catch {
    return null;
  }
}

function inViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
}

// Première image de galerie visible : dans un carrousel, seulement si c'est la diapositive affichée
function visibleMainMedia() {
  const media = document.querySelector(MAIN_MEDIA);
  if (!media || !inViewport(media)) return null;
  if (media.closest('.splide.is-initialized') && !media.classList.contains('is-active')) return null;
  return media;
}

// Image d'une carte produit visible qui mène au produit (la carte cliquée en priorité)
function cardMedia(path) {
  const cards = [lastCard, ...document.querySelectorAll('product-card[data-product-url]')];
  for (const card of cards) {
    if (!card?.isConnected || productPath(card.dataset.productUrl) !== path) continue;
    const frames = card.querySelectorAll('.is-active .product-card-media-gallery__media, .product-card-media-gallery__media');
    const media = [...frames].find(inViewport);
    if (media) return media;
  }
  return null;
}

function setName(element, name) {
  element.style.viewTransitionName = name;
  namedElements.add(element);
}

function resetNames() {
  namedElements.forEach((element) => element.style.removeProperty('view-transition-name'));
  namedElements.clear();
}

function initImageTransition() {
  document.addEventListener(
    'click',
    (event) => {
      lastCard = event.target instanceof Element ? event.target.closest('product-card') : null;
    },
    { capture: true }
  );

  // Page quittée : la carte du produit visé porte le nom. La galerie de la fiche quittée le perd,
  // sauf au retour arrière, où elle repart vers sa carte.
  window.addEventListener('pageswap', (event) => {
    if (!event.viewTransition || !event.activation) return;
    resetNames();

    const path = productPath(event.activation.entry?.url);
    const media = path ? cardMedia(path) : null;
    const mainMedia = document.querySelector(MAIN_MEDIA);

    if (media) {
      if (mainMedia) setName(mainMedia, 'none');
      setName(media, IMAGE_NAME);
    } else if (mainMedia && !(event.activation.navigationType === 'traverse' && visibleMainMedia())) {
      setName(mainMedia, 'none');
    }
  });

  // Page retrouvée par le retour arrière : l'image de la fiche revient sur sa carte
  window.addEventListener('pagereveal', (event) => {
    resetNames();
    const activation = window.navigation?.activation;
    if (!event.viewTransition || activation?.navigationType !== 'traverse' || !activation.from) return;

    const path = productPath(activation.from.url);
    const media = path ? cardMedia(path) : null;
    if (!media) return;

    const mainMedia = document.querySelector(MAIN_MEDIA);
    if (mainMedia) setName(mainMedia, 'none');
    setName(media, IMAGE_NAME);
    event.viewTransition.finished.finally(resetNames);
  });
}

/* 3. Fond sombre partagé autour des avis ---------------------------------------------------- */

const TEMPLATE_SECTION = '.shopify-section[id^="shopify-section-template--"]';
const SCHEME_CLASS = /(?:^|\s)(color-scheme-[\w-]+)/;
// Voisines laissées telles quelles : zone produit (collante), menu d'ancres, applications
const SKIP_TONE = ':has(.product-section, ud-product-nav, .shopify-app-block)';
// La section sombre est « en cours » tant qu'elle touche la bande qui va de 30 à 80 % de la hauteur
// de l'écran : son haut passe à 80 % (elle approche), son bas remonte à 30 % (elle se termine).
const TONE_BAND = '-30% 0px -20% 0px';
// Durée de la classe qui fait glisser les couleurs (0,6 s dans custom.css, section 19)
const TONE_SWITCH = 700;

const tones = new Map();

function schemeOf(element) {
  return element?.getAttribute('class')?.match(SCHEME_CLASS)?.[1] ?? null;
}

// Élément qui porte le nuancier de la section et couvre toute sa hauteur
function schemeRoot(section) {
  const root = section.matches('[class*="color-scheme-"]') ? section : section.querySelector('[class*="color-scheme-"]');
  return root && root.offsetHeight >= section.offsetHeight * 0.9 ? root : null;
}

function isDark(element) {
  const [r, g, b, alpha = 1] = (getComputedStyle(element).backgroundColor.match(/[\d.]+/g) ?? []).map(Number);
  return alpha > 0.5 && 0.2126 * r + 0.7152 * g + 0.0722 * b < 80;
}

function neighbour(section, direction) {
  let node = section[direction];
  while (node && !(node.matches(TEMPLATE_SECTION) && node.offsetHeight > 0)) node = node[direction];
  return node;
}

// Une voisine change de nuancier avec ses éléments du même nuancier (ils forment son fond) ;
// les cartes d'un autre nuancier (crème, gris) restent telles quelles.
function neighbourTone(section) {
  if (!section || section.matches(SKIP_TONE)) return null;
  if (tones.has(section)) return tones.get(section);

  const root = schemeRoot(section);
  const scheme = schemeOf(root);
  if (!scheme) return null;

  const tone = { root, scheme, current: scheme, count: 0, timer: 0 };
  tone.elements = [root, ...root.querySelectorAll(`.${CSS.escape(scheme)}`)];
  tones.set(section, tone);
  section.classList.add('ud-tone-section');
  return tone;
}

function setTone(zone, active) {
  for (const tone of zone.neighbours) {
    tone.count += active ? 1 : -1;
    const target = tone.count > 0 ? zone.scheme : tone.scheme;
    if (target === tone.current) continue;

    tone.root.classList.add('ud-tone-switching');
    tone.elements.forEach((element) => element.classList.replace(tone.current, target));
    tone.current = target;
    clearTimeout(tone.timer);
    tone.timer = setTimeout(() => tone.root.classList.remove('ud-tone-switching'), TONE_SWITCH);
  }
}

function initTone() {
  const candidates = new Set(document.querySelectorAll(`${TEMPLATE_SECTION} .ud-immersive`));
  const reviews = document.getElementById('avis');
  if (reviews?.closest(TEMPLATE_SECTION)) candidates.add(reviews);

  const zones = [...candidates].flatMap((element) => {
    const section = element.closest(TEMPLATE_SECTION);
    const root = schemeRoot(section);
    const scheme = schemeOf(root);
    if (!scheme || !isDark(root)) return [];

    const neighbours = ['previousElementSibling', 'nextElementSibling']
      .map((direction) => neighbourTone(neighbour(section, direction)))
      .filter((tone) => tone && tone.scheme !== scheme);
    if (neighbours.length === 0) return [];

    // Liseré d'un pixel en haut et en bas (custom.css, section 19) : sans lui, une fine ligne
    // claire apparaissait à la jonction avec une voisine assombrie quand la limite tombe entre deux pixels.
    // Sa section ne coupe plus en vertical, sinon le liseré serait rogné à la limite.
    root.classList.add('ud-tone-zone');
    section.classList.add('ud-tone-section');
    return [{ root, scheme, neighbours, active: false }];
  });
  if (zones.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const zone = zones.find(({ root }) => root === entry.target);
        if (!zone || zone.active === entry.isIntersecting) continue;
        zone.active = entry.isIntersecting;
        setTone(zone, zone.active);
      }
    },
    { rootMargin: TONE_BAND }
  );
  zones.forEach(({ root }) => observer.observe(root));
}

if (motionAllowed) {
  if ('IntersectionObserver' in window) {
    initReveal();
    initTone();
  }
  initImageTransition();
}
